import { Component, OnInit, OnChanges, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { NgbPanelChangeEvent } from '@ng-bootstrap/ng-bootstrap';
import { DataService } from '../../services/data/data.service';
import { ConfigService } from '../../services/config/config.service';
import { PurchaseOrderService } from '../../services/purchase-order/purchase-order.service';
import { ContractorService } from '../../services/contractor/contractor.service';
import { ValidationService } from '../../services/validation/validation.service';
import _ from 'lodash';
import { DataFormService } from '../../services/data-form/data-form.service';

@Component({
	selector: 'pcm-data-page',
	templateUrl: './data-page.component.html',
	styleUrls: ['./data-page.component.scss']
})

export class DataPageComponent implements OnInit, OnChanges, OnDestroy {

	@Input() title = '';
	@Input() helpText = '';

	// only first level fields will be displayed nested fields will be ignored
	@Input() fieldsOnly = false;

	// the prop of the nested object to display -- others will be ignored
	@Input() onlyObject = '';

	// only show editable fields
	@Input() onlyEdits = false;

	// overriding other inputs and settings to force editable fields to show as editable inputs
	@Input() forceEdits = false;

	// force the showing of the apply/submit button even if form is invalid or submit is otherwise disabled
	@Input() forceApply = false;

	// show editable fields along with non editable fields
	@Input() alsoEdits = false;

	// only show approval fields
	@Input() onlyApprovals = false;

	@Input() allowCancel = false;
	@Input() allowDelete = false;
	@Input() allowClose = false;
	@Input() aclOverwrite = false;
	@Input() allowReject = false;

	// applies "all-clean" css class -- see styles for effect
	@Input() allClean = false;
	@Input() showUnsaved = false;
	@Input() showObjectTitle = true;

	// use lax requirements instead of normal required fields (see "Lax Required" config csv column)
	@Input() useLax = false;
	@Input() commentPlaceholder = '';
	@Input() accordianArrays = true;
	@Input() boldFirst = true;
	@Input() forceDisabled = false;

	// use form state such as form.pristine
	@Input() useFormState = false;

	@Input() pageData;
	@Input() dataConfig;
	@Input() applyLabel = 'Apply';

	// optional number to show only a number of fields by default, with an option to expand to all
	// note: this only works with standard non-editable fields currently
	@Input() collapseToNumRows: number;

	@Input() initForm = false;
	@Input() hasJustAddedContractor = false;

	@Output() formSubmit: EventEmitter<any> = new EventEmitter();
	@Output() formCancel: EventEmitter<any> = new EventEmitter();
	@Output() formDelete: EventEmitter<any> = new EventEmitter();
	@Output() formReject: EventEmitter<any> = new EventEmitter();
	//
	@Output() addItem: EventEmitter<any> = new EventEmitter();

	//
	@Output() contentApprove: EventEmitter<any> = new EventEmitter();
	//
	@Output() dataInit: EventEmitter<any> = new EventEmitter();
	@Output() dataChange: EventEmitter<any> = new EventEmitter();

	pageFormGroup: UntypedFormGroup;

	// most likely unused -- local variable in getActivePanels() method is used instead
	activePanels = '';

	// utilized in hasData() method and reset on component destruction -- helps with cache issues
	dataInitSent = false;

	canSubmit = true;

	// used in various situations where original data is required since pageData will be modifies through data binding
	// also sent out and used externally during pageFormSubmit event
	originalPageData = {};

	// most likely unused (never updated) although it is sent out in pageFormSubmit event
	dataChanged = false;

	pageDataUpdateListener;
	isCollapsed = true;
	standardFields = [];

	constructor(
		public dataService: DataService,
		public configService: ConfigService,
		public purchaseOrderService: PurchaseOrderService,
		public contractorService: ContractorService,
		private validationService: ValidationService,
		private formBuilder: UntypedFormBuilder,
		private dataFormService: DataFormService
	) { }

	ngOnInit() {
		this.init();

		this.pageDataUpdateListener = this.dataService.pageDataUpdateListener().subscribe((event) => {
			if (this.pageFormGroup) {
				this.configService.injectFormData(this.dataConfig, this.pageFormGroup, event.data, [this.configService.contractorAddonsProp]);
			}
		});
	}

	init() {
		this.originalPageData = _.cloneDeep(this.pageData);
		console.log('DATA PAGE INIT', this.title, this.dataConfig, this.pageData, this.originalPageData);
		this.dataService.clearInputData();
		this.checkCustomInputData();
		if (this.onlyEdits || this.alsoEdits || this.forceEdits) {
			const validations = this.validationService.applyValidation(this.dataConfig, this.forceEdits);
			this.pageFormGroup = this.formBuilder.group(validations);
			this.pageFormGroup['config'] = this.dataConfig.fields;
			this.pageFormGroup = this.validationService.applyControlProps(this.dataConfig, this.pageFormGroup);
			// this.checkCustomFormControls();
			this.configService.injectFormData(this.dataConfig, this.pageFormGroup, this.pageData, [this.configService.contractorAddonsProp]);
		}
	}

	ngOnChanges(event) {
		// console.log('DATA PAGE CHANGE');
		if (this.initForm) {
			this.init();
		}
		this.dataChange.emit({
			event: event,
			data: this.pageData
		});
	}

	ngOnDestroy() {
		// Reset dataInitSent param on destroy so its sent again when reinitialized
		this.dataInitSent = false;
		this.pageData = undefined;
		this.pageDataUpdateListener.unsubscribe();
	}

	editFieldsChange(event) {
		// console.log('EDIT FIELDS CHANGE');
		this.dataChange.emit({
			event: event,
			data: this.pageData
		});
	}

	withEdits(item) {
		let withEdits = true;
		if (this.alsoEdits && item.edit) {
			// dont show if also showing item in edit form
			withEdits = false;
		}
		return withEdits;
	}

	hasData(event?) {
		let hasData = false;
		const self = this;
		if (this.dataConfig.fields !== undefined) {
			this.dataConfig.fields.forEach(function (d, i) {
				if (self.pageData[d['prop']] !== undefined) {
					hasData = true;
				}
			});
			if (!this.dataInitSent && hasData) {
				this.dataInitSent = true;
				this.dataInit.emit({
					event: event,
					data: this.pageData
				});
			}
		}
		// console.log('HAS DATA', hasData, this.dataConfig.fields, this.pageData, this.dataInitSent);
		return hasData;
		// pageData[this.dataConfig.fields[0]['prop']]!==undefined
	}

	pageAddItem(event, uuid) {
		console.log('PAGE ADD ITEM', uuid);
		this.addItem.emit({
			event: event,
			data: uuid
		});
	}

	pageFormSubmit(event) {
		console.log('PAGE FORM SUBMIT', event, this.pageData, this.dataConfig);

		const extractedData = this.configService.extractFormData(
			this.dataConfig, this.pageFormGroup, [this.configService.validateCalendarEntriesProp], [this.configService.contractorAddonsProp]);
		const updatedData = this.configService.mergeFormData(
			this.dataConfig, extractedData, this.pageData, [this.configService.validateCalendarEntriesProp], [this.configService.contractorAddonsProp]);
		console.log('PAGE FORM SUBMIT', this.pageData, extractedData, updatedData, this.pageFormGroup);

		this.pageData = updatedData;
		this.updateCustomInputData();

		this.formSubmit.emit({
			event: event,
			data: this.pageData,
			originalData: this.originalPageData,
			dataChanged: this.dataChanged
		});
	}


	pageFormCancel(event) {
		this.formCancel.emit(event);
	}

	pageFormReject(event) {
		this.formReject.emit(event);
	}

	pageFormDelete(event) {
		console.log('PAGE FORM DELETE', event);
		this.formDelete.emit({
			event: event,
			data: this.pageData
		});
	}

	arrayItemClick(event, prop) {
		console.log('ARRAY ITEM CLICK', prop, event);

		// when an array item is clicked we need to see what to do with it, other than expand the accordion in the UI
		const clickedTitle = event.target.title;
		if (clickedTitle === 'panel-title') {
			switch (prop) {
				// If the array clicked is a contractor item show new contractor form or existing contractor record
				case 'shorttext':
					const clickedValue = event.target.id;
					this.purchaseOrderService.showAvailableContractor(clickedValue, 'uuid', this.configService.getDataObj(this.configService.contractorDataObj));
					return;
			}
		}
	}

	approveContent(event) {
		this.contentApprove.emit({
			event: event,
			data: this.pageData
		});
	}

	updateCustomInputData() {
		const self = this;
		// apply acl data as needed
		this.pageData = this.dataService.applyACL(this.pageData, this.configService.personas, this.dataConfig.fields, this.aclOverwrite);
		console.log('UPDATED CUSTOM INPUT DATA', this.pageData);
	}

	checkCustomInputData() {
		// console.log('CHECK CUSTOM INPUT DATA', this.dataConfig.fields, this.pageData);
		const self = this;
		// Loop through data config to find fields with special validations
		this.dataConfig.fields?.forEach(function (d, i) {
			// If quantity type validation see if page data has a value, if not populate with first option
			if (d.validate === self.configService.validateQuantityTypeProp) {
				if (self.pageData[d.prop] === undefined || self.pageData[d.prop] === '') {
					self.pageData[d.prop] = self.dataService.quantityTypes[0].type;
				}
			}

			// if approval validation see if page data has a value, if not populate with default of approved
			if (d.validate === self.configService.validateApprovalProp) {
				if (self.pageData[d.prop] === undefined || self.pageData[d.prop] === '') {
					self.pageData[d.prop] = self.configService.timesheetApprovalStatus;
				}
			}
		});
		// show acl data as needed
		this.pageData = this.dataService.showACL(this.pageData, this.configService.personas, this.dataConfig.fields, this.configService);
	}

	checkCustomFormControls() {
		// console.log('CHECK CUSTOM INPUT DATA', this.dataConfig.fields, this.pageData);
		const self = this;
		// Loop through data config to find fields with special validations
		this.dataConfig.fields.forEach(function (d, i) {
			// If quantity type validation see if page data has a value, if not populate with first option
			if (d.validate === self.configService.validateQuantityTypeProp) {
				if (self.configService.getSingleFormData(d.prop, this.pageFormGroup) === undefined
					|| self.configService.getSingleFormData(d.prop, this.pageFormGroup) === '') {
						this.configService.injectSingleFormData(d.prop, this.pageFormGroup, this.dataService.quantityTypes[0].type);
				}
			}

			// if approval validation see if page data has a value, if not populate with default of approved
			if (d.validate === self.configService.validateApprovalProp) {
				if (self.configService.getSingleFormData(d.prop, this.pageFormGroup) === undefined
					|| self.configService.getSingleFormData(d.prop, this.pageFormGroup) === '') {
					this.configService.injectSingleFormData(d.prop, this.pageFormGroup, self.configService.timesheetApprovalStatus);
				}
			}
		});
	}

	roleChecked(event, role) {
		console.log('ROLE CHECKED', role, event);
		const isChecked = event.target.checked;
		let activeRoles = [];
		if (this.dataService.userDetails['role'] !== undefined) {
			activeRoles = this.dataService.userDetails['role'].split(',');
		}
		if (!isChecked) {
			activeRoles.splice(activeRoles.indexOf(role), 1);
		} else {
			activeRoles.push(role);
		}
		const roleString = activeRoles.join();
		// const newDetails = _.cloneDeep(this.dataService.userDetails);
		// newDetails['role'] = roleString;
		// this.dataService.updateUser(newDetails);
		this.dataService.userDetails['role'] = roleString;
		console.log('NEW ROLES', this.dataService.userDetails['role']);
	}

	roleCheck(role) {
		let isActive = false;
		let activeRoles = [];
		if (this.dataService.userDetails['role'] !== undefined) {
			activeRoles = this.dataService.userDetails['role'].split(',');
		}
		activeRoles.forEach(function (d, i) {
			if (d === role) {
				isActive = true;
			}
		});
		// console.log('ROLE CHECK', role, isActive);
		return isActive;
	}

	getArrayItemTitle(arrayItem, fields, prop) {
		let arrayItemTitle = '';
		let keepLooking = true;
		let firstMatch = -1;
		fields.forEach(function (d, i) {
			if (d[prop] && keepLooking) {
				keepLooking = false;
				firstMatch = i;
			}
		});
		if (firstMatch > -1) {
			arrayItemTitle = arrayItem[fields[firstMatch].prop];
		}
		// console.log('GET ARRAY ITEM TITLE', arrayItemTitle, arrayItem, fields, prop);
		return arrayItemTitle;
	}

	getArrayTitle(index, obj, prop) {
		// console.log('GET ARRAY TITLE', index, obj, prop);

		let title = '';
		try {
			title = obj.name;
		} catch (e) {}

		return title;

	}

	getArrayItemIdProp(fields, prop) {
		let idProp = '';
		let keepLooking = true;
		let firstMatch = -1;
		fields.forEach(function (d, i) {
			if (d[prop] && keepLooking) {
				keepLooking = false;
				firstMatch = i;
			}
		});
		if (firstMatch > -1) {
			idProp = fields[firstMatch][this.configService.idProp];
		}
		return idProp;
	}

	getArrayItemId(arrayItem, fields, prop) {
		return arrayItem[this.getArrayItemIdProp(fields, prop)];
	}

	getArrayProp(fields, prop) {
		let arrayItemProp = '';
		let keepLooking = true;
		let firstMatch = -1;
		fields.forEach(function (d, i) {
			if (d[prop] && keepLooking) {
				keepLooking = false;
				firstMatch = i;
			}
		});
		if (firstMatch > -1) {
			arrayItemProp = fields[firstMatch].prop;
		}
		return arrayItemProp;
	}

	getActivePanels(prefix, arr) {
		// console.log('GET ACTIVE PANELS', prefix, arr);
		let activePanels = '';
		if (arr.length === 1) {
			activePanels = prefix + '0';
		} else {
			activePanels = this.purchaseOrderService.getActivePanels();
		}
		// console.log('DATA PAGE ACTIVE PANELS', activePanels);
		return activePanels;
	}

	public beforeChange($event: NgbPanelChangeEvent) {
		console.log('BEFORE CHANGE: ', $event);
	}

	updateCanSubmit(event) {
		this.canSubmit = event.data;
	}

	updateUseFormState(event) {
		this.useFormState = false;
	}

	shouldShow(item) {
		return this.dataFormService.shouldShow(item, undefined, this.pageData);
	}

	checkIfAddNewContractorButtonDisabled(lineItemData) {
		return this.hasJustAddedContractor || this.purchaseOrderService.isPOCancelled() || !this.contractorService.hasAddRoom(lineItemData);
	}

	getStandardFields() {
		this.standardFields = this.dataConfig.fields.filter((field) => {
			return field.view
				&& field.page
				&& this.withEdits(field)
				&& this.pageData[field.prop] !== ''
				&& this.pageData[field.prop] !== undefined
				&& this.shouldShow(field);
		});
		return this.standardFields;
	}

	toggleCollapsedToNumRows() {
		this.isCollapsed = !this.isCollapsed;
	}
}
